import React, { useEffect, useState } from "react";
import {
  ZOOM_STEPS,
  applyCameraZoom,
  getCameraVideoElement,
} from "../../utils/cameraZoom";
import "./style.css";

export default function CameraZoomControls({
  rootRef,
  enabled = true,
  zoom,
  onZoomChange,
}) {
  const [localZoom, setLocalZoom] = useState(zoom ?? 1);
  const activeZoom = zoom ?? localZoom;

  useEffect(() => {
    if (zoom != null) {
      setLocalZoom(zoom);
    }
  }, [zoom]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const root = rootRef?.current;
    const apply = () => {
      const video = getCameraVideoElement(root);
      if (video) {
        applyCameraZoom(video, activeZoom);
      }
    };

    apply();
    const timer = window.setInterval(apply, 800);
    return () => window.clearInterval(timer);
  }, [activeZoom, enabled, rootRef]);

  if (!enabled) {
    return null;
  }

  const setZoom = (next) => {
    const clamped = Math.min(
      ZOOM_STEPS[ZOOM_STEPS.length - 1],
      Math.max(ZOOM_STEPS[0], next)
    );
    setLocalZoom(clamped);
    onZoomChange?.(clamped);
    const video = getCameraVideoElement(rootRef?.current);
    applyCameraZoom(video, clamped);
  };

  const stepIndex = ZOOM_STEPS.reduce((best, step, index) => {
    return Math.abs(step - activeZoom) < Math.abs(ZOOM_STEPS[best] - activeZoom)
      ? index
      : best;
  }, 0);

  return (
    <div className="pi-zoom-controls" role="group" aria-label="Camera zoom">
      <button
        type="button"
        className="pi-zoom-controls__btn"
        aria-label="Zoom out"
        disabled={stepIndex <= 0}
        onClick={(e) => {
          e.stopPropagation();
          setZoom(ZOOM_STEPS[Math.max(0, stepIndex - 1)]);
        }}
      >
        −
      </button>
      <div className="pi-zoom-controls__steps">
        {ZOOM_STEPS.map((step) => (
          <button
            key={step}
            type="button"
            className={`pi-zoom-controls__chip${
              Math.abs(activeZoom - step) < 0.05 ? " is-active" : ""
            }`}
            aria-label={`${step}x zoom`}
            aria-pressed={Math.abs(activeZoom - step) < 0.05}
            onClick={(e) => {
              e.stopPropagation();
              setZoom(step);
            }}
          >
            {step % 1 === 0 ? `${step}x` : `${step}x`}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="pi-zoom-controls__btn"
        aria-label="Zoom in"
        disabled={stepIndex >= ZOOM_STEPS.length - 1}
        onClick={(e) => {
          e.stopPropagation();
          setZoom(ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, stepIndex + 1)]);
        }}
      >
        +
      </button>
    </div>
  );
}
